<template>
  <div v-loading="detailLoading" class="gen-summary">
    <!-- 未选择记录提示 -->
    <div v-if="!currentRecord" class="gen-summary-empty">
      <div class="gen-summary-empty-icon"><i class="el-icon-document"></i></div>
      <div class="gen-summary-empty-title">总结报告</div>
      <div class="gen-summary-empty-desc">暂无签到表识别整合记录，请先完成签到表整合</div>
    </div>

    <div v-else>
      <!-- 顶部操作栏 -->
      <div class="gen-summary-toolbar">
        <el-button type="primary" size="small" icon="el-icon-view" :loading="previewLoading" @click="handlePreviewReport">预览报告</el-button>
        <el-button type="success" size="small" icon="el-icon-folder-opened" :loading="paramSaving" @click="handleSaveParam">保存参数</el-button>
        <el-button type="warning" size="small" icon="el-icon-download" :loading="exportLoading" @click="handleExportWord">生成 Word</el-button>
      </div>

      <!-- ===== 第一行：报告参数 → AI 正文结果 ===== -->
      <div class="gen-summary-row">
        <!-- 报告参数卡片（保存到主表 wordTextUser；每个字段独占一行） -->
        <div class="gen-summary-card gen-summary-card--param">
            <div class="gen-summary-card-header">
              <span class="gen-summary-card-title"><i class="el-icon-edit-outline"></i> 报告参数填写</span>
              <span class="gen-summary-card-hint">课程名称、客户、培训对象等通用参数（独立保存）</span>
            </div>
            <div class="gen-summary-card-body">
              <div class="gen-summary-form-stack">
                <div ref="paramItem-courseName" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.courseName }">
                  <div class="gen-summary-label gen-summary-label--required">课程名称</div>
                  <el-input v-model="paramForm.courseName" size="small" placeholder="请输入课程名称" @input="clearParamError('courseName')" />
                </div>
                <div class="gen-summary-form-item">
                  <div class="gen-summary-label">主讲老师</div>
                  <el-input v-model="paramForm.mainTeacher" size="small" placeholder="请输入主讲老师" />
                </div>
                <div ref="paramItem-customerName" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.customerName }">
                  <div class="gen-summary-label gen-summary-label--required">客户名称</div>
                  <el-input v-model="paramForm.customerName" size="small" placeholder="请输入客户名称" @input="clearParamError('customerName')" />
                </div>
                <div ref="paramItem-trainObject" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.trainObject }">
                  <div class="gen-summary-label gen-summary-label--required">培训对象</div>
                  <el-input v-model="paramForm.trainObject" size="small" placeholder="请输入培训对象" @input="clearParamError('trainObject')" />
                </div>
                <div ref="paramItem-projectManagerTeam" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.projectManagerTeam }">
                  <div class="gen-summary-label gen-summary-label--required">项目经理团队</div>
                  <div class="gen-summary-field-hint">执行人员加市场</div>
                  <el-input v-model="paramForm.projectManagerTeam" size="small" placeholder="请输入项目经理团队" @input="clearParamError('projectManagerTeam')" />
                </div>
                <div ref="paramItem-projectTeamContact" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.projectTeamContact }">
                  <div class="gen-summary-label gen-summary-label--required">项目团队联系方式</div>
                  <div class="gen-summary-field-hint">执行经理电话</div>
                  <el-input v-model="paramForm.projectTeamContact" size="small" placeholder="请输入项目团队联系方式" @input="clearParamError('projectTeamContact')" />
                </div>
                <div ref="paramItem-trainPlace" class="gen-summary-form-item" :class="{ 'is-error': paramErrors.trainPlace }">
                  <div class="gen-summary-label gen-summary-label--required">培训地点</div>
                  <el-input v-model="paramForm.trainPlace" size="small" placeholder="请输入培训地点" @input="clearParamError('trainPlace')" />
                </div>
              </div>
            </div>
          </div>

        <!-- 箭头：报告参数 → AI 正文 -->
        <div class="gen-summary-arrow">
          <i class="el-icon-d-arrow-right"></i>
        </div>

        <!-- AI 正文结果卡片（培训目标/授课方式/课程背景/改善建议1/2，独立可编辑模块） -->
        <div class="gen-summary-card gen-summary-card--ai">
            <div class="gen-summary-card-header">
              <span class="gen-summary-card-title"><i class="el-icon-magic-stick"></i> AI 生成结果</span>
              <div class="gen-summary-card-header-ops">
                <el-tag v-if="hasAiResult" type="success" size="mini" effect="plain">已生成</el-tag>
                <el-tag v-else type="info" size="mini" effect="plain">待生成</el-tag>
                <el-button v-if="hasAiResult && !isAiParamEditing" type="primary" size="mini" icon="el-icon-edit" @click="startEditAiParam">编辑</el-button>
              </div>
            </div>
            <div class="gen-summary-card-body gen-summary-card-body--aiparam">
              <!-- 无结果提示 -->
              <div v-if="!hasAiResult" class="gen-summary-ai-empty">
                <div class="gen-summary-ai-empty-icon"><i class="el-icon-magic-stick"></i></div>
                <div class="gen-summary-ai-empty-text">填写课程内容后点击"AI 生成"</div>
                <div class="gen-summary-ai-empty-hint">系统将根据每门课程自动生成背景、目标、建议</div>
              </div>

              <template v-else>
                <!-- 只读展示 -->
                <template v-if="!isAiParamEditing">
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">培训目标</div>
                    <div class="gen-summary-readonly">{{ mainAi.trainGoal || '-' }}</div>
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">授课方式</div>
                    <div class="gen-summary-readonly">{{ mainAi.trainMethod || '-' }}</div>
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">课程背景</div>
                    <div class="gen-summary-readonly">{{ mainAi.courseBackground || '-' }}</div>
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">改善建议1</div>
                    <div class="gen-summary-readonly">{{ mainAi.improveSuggestionOne || '-' }}</div>
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">改善建议2</div>
                    <div class="gen-summary-readonly">{{ mainAi.improveSuggestionTwo || '-' }}</div>
                  </div>
                </template>

                <!-- 编辑模式 -->
                <template v-else>
                  <div class="gen-summary-edit-banner">
                    <i class="el-icon-info"></i>
                    <span>编辑 AI 生成的正文内容，保存后将更新到主表</span>
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">培训目标</div>
                    <el-input v-model="aiParamEditForm.trainGoal" type="textarea" :rows="3" placeholder="培训目标" />
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">授课方式</div>
                    <el-input v-model="aiParamEditForm.trainMethod" type="textarea" :rows="3" placeholder="授课方式" />
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">课程背景</div>
                    <el-input v-model="aiParamEditForm.courseBackground" type="textarea" :rows="4" placeholder="课程背景" />
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">改善建议1</div>
                    <el-input v-model="aiParamEditForm.improveSuggestionOne" type="textarea" :rows="3" placeholder="改善建议1" />
                  </div>
                  <div class="gen-summary-section">
                    <div class="gen-summary-sublabel">改善建议2</div>
                    <el-input v-model="aiParamEditForm.improveSuggestionTwo" type="textarea" :rows="3" placeholder="改善建议2" />
                  </div>
                  <div class="gen-summary-action-btns">
                    <el-button size="small" icon="el-icon-close" @click="cancelEditAiParam">取消</el-button>
                    <el-button type="success" size="small" icon="el-icon-check" :loading="aiParamSaving" @click="saveEditAiParam">保存</el-button>
                  </div>
                </template>
              </template>
            </div>
            <!-- AI 生成中特效 loading 蒙层（与课程总结卡片共用） -->
            <div v-if="courseAiLoading" class="gen-ai-loading">
              <div class="gen-ai-loading__overlay"></div>
              <div class="gen-ai-loading__halo"></div>
              <div class="gen-ai-loading__core">
                <div class="gen-ai-loading__ring"></div>
                <div class="gen-ai-loading__ring gen-ai-loading__ring--reverse"></div>
                <i class="el-icon-magic-stick gen-ai-loading__icon"></i>
              </div>
              <div class="gen-ai-loading__text">
                <span class="gen-ai-loading__title">AI 正在生成</span>
                <span class="gen-ai-loading__sub">培训目标、课程背景、改善建议合成中
                  <i class="gen-ai-loading__dot">.</i><i class="gen-ai-loading__dot">.</i><i class="gen-ai-loading__dot">.</i>
                </span>
              </div>
              <div class="gen-ai-loading__particles">
                <span v-for="n in 8" :key="n" class="gen-ai-loading__particle"></span>
              </div>
            </div>
          </div>
      </div>

      <!-- ===== 第二行：课程信息 → 课程总结/讲师简介 ===== -->
      <div class="gen-summary-row">
        <!-- 课程内容记录卡片（多条课程，轮播切换） -->
        <div class="gen-summary-card gen-summary-card--course">
            <div class="gen-summary-card-header">
              <span class="gen-summary-card-title"><i class="el-icon-collection"></i> 课程信息</span>
              <div class="gen-course-ops">
                <el-button type="primary" size="mini" :disabled="courseListBusy" @click="handleAddCourse">
                  <i class="el-icon-plus gen-course-add-icon"></i>
                  <span>新增课程</span>
                </el-button>
              </div>
            </div>

            <!-- 课程轮播切换条 -->
            <div class="gen-course-carousel">
              <el-button class="gen-course-arrow" icon="el-icon-arrow-left" circle size="mini" :disabled="courseList.length <= 1 || currentIndex === 0" @click="switchCourse(currentIndex - 1)" />
              <div class="gen-course-track">
                <div v-for="(c, idx) in courseList" :key="c._uid" class="gen-course-tab" :class="{ 'is-active': idx === currentIndex }" @click="switchCourse(idx)">
                  <span class="gen-course-tab__name">课程{{ chineseNumber(idx + 1) }}</span>
                  <span class="gen-course-tab__status" :class="'is-' + courseStatusOf(c)">{{ courseStatusText(c) }}</span>
                  <i v-if="idx > 0" class="el-icon-close gen-course-tab__del" @click.stop="handleDeleteCourse(c, idx)" />
                </div>
              </div>
              <el-button class="gen-course-arrow" icon="el-icon-arrow-right" circle size="mini" :disabled="courseList.length <= 1 || currentIndex >= courseList.length - 1" @click="switchCourse(currentIndex + 1)" />
            </div>

            <!-- 当前课程编辑区（子组件） -->
            <div class="gen-summary-card-body">
              <div v-if="currentCourse" class="gen-course-current-title">
                <i class="el-icon-reading"></i>
                <span>课程{{ chineseNumber(currentIndex + 1) }}</span>
                <span class="gen-course-current-title__hint">每个课程独立保存，AI 智能提取将填充到当前课程</span>
              </div>
              <report-gen-summary-course
                v-if="currentCourse"
                ref="courseEditor"
                :course="currentCourse"
                @busy-change="onCourseBusy"
                @saved="onCourseSaved"
                @ai-generating="onCourseAiGenerating"
                @request-ai="onCourseRequestAi"
              />
            </div>
          </div>

        <!-- 箭头：课程信息 → 课程总结/讲师简介 -->
        <div class="gen-summary-arrow">
          <i class="el-icon-d-arrow-right"></i>
        </div>

        <!-- 课程总结 + 讲师简介（AI）卡片 -->
        <div class="gen-summary-card gen-summary-card--ai gen-summary-card--merge">
            <div class="gen-summary-card-header">
              <span class="gen-summary-card-title"><i class="el-icon-document-copy"></i> 课程总结 / 讲师简介</span>
              <div class="gen-summary-card-header-ops">
                <span class="gen-summary-card-hint">课程汇总预览</span>
                <el-tag type="info" size="mini" effect="plain">共 {{ courseList.length }} 门课程</el-tag>
              </div>
            </div>
            <div class="gen-summary-card-body gen-summary-card-body--merge">
              <!-- 无结果提示 -->
              <div v-if="!hasAnyCourseAi" class="gen-summary-ai-empty">
                <div class="gen-summary-ai-empty-icon"><i class="el-icon-magic-stick"></i></div>
                <div class="gen-summary-ai-empty-text">提交课程后将生成总结与讲师简介</div>
                <div class="gen-summary-ai-empty-hint">在左侧课程卡片中点击"AI 生成"触发</div>
              </div>

              <template v-else>
                <!-- 只读合并展示 -->
                <template v-if="!isAiEditing">
                  <!-- 课程总结 -->
                  <div class="gen-summary-merge-block">
                    <div class="gen-summary-merge-block__title">
                      <i class="el-icon-tickets"></i>
                      <span>课程总结</span>
                      <span class="gen-summary-merge-block__hint">{{ courseList.length > 1 ? `共 ${courseList.length} 门课程，已合并展示` : '' }}</span>
                      <el-button type="primary" size="mini" icon="el-icon-edit" @click="startEditAi('summary')">编辑</el-button>
                    </div>
                    <div class="gen-summary-readonly gen-summary-readonly--merge">{{ mergedCourseSummary || '-' }}</div>
                  </div>

                  <!-- 讲师简介（AI优化） -->
                  <div class="gen-summary-merge-block">
                    <div class="gen-summary-merge-block__title">
                      <i class="el-icon-user"></i>
                      <span>讲师简介（AI 优化）</span>
                      <span class="gen-summary-merge-block__hint">{{ courseList.length > 1 ? `共 ${courseList.length} 门课程，已合并展示` : '' }}</span>
                      <el-button type="primary" size="mini" icon="el-icon-edit" @click="startEditAi('teacher')">编辑</el-button>
                    </div>
                    <div class="gen-summary-readonly gen-summary-readonly--merge">{{ mergedTeacherAi || '-' }}</div>
                  </div>
                </template>

                <!-- 编辑模式：拆分多课程 -->
                <template v-else>
                  <div class="gen-summary-edit-banner">
                    <i class="el-icon-info"></i>
                    <span>{{ aiEditTarget === 'summary' ? '课程总结' : '讲师简介（AI 优化）' }}：共 {{ courseList.length }} 门课程，请逐个编辑后保存</span>
                  </div>
                  <div v-for="(c, idx) in courseList" :key="'edit-' + c._uid" class="gen-summary-edit-item">
                    <div class="gen-summary-sublabel">课程{{ chineseNumber(idx + 1) }}</div>
                    <el-input
                      v-if="aiEditTarget === 'summary'"
                      v-model="c._editWordCourseSummary"
                      type="textarea"
                      :rows="4"
                      :placeholder="'请输入课程' + chineseNumber(idx + 1) + '的课程总结'"
                    />
                    <el-input
                      v-else
                      v-model="c._editWordTeacherAi"
                      type="textarea"
                      :rows="4"
                      :placeholder="'请输入课程' + chineseNumber(idx + 1) + '的讲师简介（AI 优化）'"
                    />
                  </div>
                  <div class="gen-summary-action-btns">
                    <el-button size="small" icon="el-icon-close" @click="cancelEditAi">取消</el-button>
                    <el-button type="success" size="small" icon="el-icon-check" :loading="aiEditSaving" @click="saveEditAi">保存</el-button>
                  </div>
                </template>
              </template>
            </div>
            <!-- AI 生成中特效 loading 蒙层 -->
            <div v-if="courseAiLoading" class="gen-ai-loading">
              <div class="gen-ai-loading__overlay"></div>
              <div class="gen-ai-loading__halo"></div>
              <div class="gen-ai-loading__core">
                <div class="gen-ai-loading__ring"></div>
                <div class="gen-ai-loading__ring gen-ai-loading__ring--reverse"></div>
                <i class="el-icon-magic-stick gen-ai-loading__icon"></i>
              </div>
              <div class="gen-ai-loading__text">
                <span class="gen-ai-loading__title">AI 正在生成</span>
                <span class="gen-ai-loading__sub">课程总结与讲师简介合成中
                  <i class="gen-ai-loading__dot">.</i><i class="gen-ai-loading__dot">.</i><i class="gen-ai-loading__dot">.</i>
                </span>
              </div>
              <div class="gen-ai-loading__particles">
                <span v-for="n in 8" :key="n" class="gen-ai-loading__particle"></span>
              </div>
            </div>
          </div>
      </div>
    </div>

    <!-- 预览操作类型选择弹框 -->
    <el-dialog title="预览报告" :visible.sync="previewDialogVisible" width="420px" append-to-body :close-on-click-modal="false">
      <div style="text-align:center; padding: 10px 0;">
        <p style="margin:0 0 16px; color:#606266;">检测到已有报告文件，请选择预览方式</p>
        <div style="display:flex; gap:12px; justify-content:center;">
          <el-button type="primary" plain @click="handlePreviewConfirm(1)"><i class="el-icon-view" style="margin-right:4px;"></i>预览上次报告</el-button>
          <el-button type="warning" plain @click="handlePreviewConfirm(2)"><i class="el-icon-refresh" style="margin-right:4px;"></i>重新生成并预览</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 多份评估表时，导出/预览前选择评估表弹窗 -->
    <el-dialog title="选择评估表" :visible.sync="ratingPickVisible" width="420px" append-to-body :close-on-click-modal="false" @close="handleRatingPickCancel">
      <div style="padding: 10px 4px;">
        <p style="margin:0 0 14px; font-size:13px; color:#475569;">当前存在多份评估表，请选择用于生成报告的评估表：</p>
        <el-select v-model="ratingPickForm.selectedId" placeholder="请选择评估表" size="small" style="width:100%;">
          <el-option
            v-for="(r, idx) in ratingPickList"
            :key="r.rrmusmRatingId"
            :label="'评估' + (idx + 1)"
            :value="r.rrmusmRatingId"
          ></el-option>
        </el-select>
      </div>
      <div slot="footer">
        <el-button size="small" @click="handleRatingPickCancel">取消</el-button>
        <el-button size="small" type="primary" @click="handleRatingPickConfirm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getRtReportModelUserSignMergeList,
  updateRtReportModelUserSignMergeWordParam,
  updateRtReportModelUserSignMergeWordText,
  getRtReportModelUserSignMergeWordList,
  updateRtReportModelUserSignMergeWord,
  deleteRtReportModelUserSignMergeWord,
  exportRtReportModelUserSignMergeReportWord,
  previewRtReportModelUserSignMergeReportWord
} from '@/api/api'
import ReportGenSummaryCourse from './reportGenSummaryCourse.vue'

// 中文数字转换（最多支持到几十）
const CN_NUMS = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
function toChineseNumber (n) {
  if (n <= 0) return CN_NUMS[0]
  if (n < 11) return CN_NUMS[n]
  if (n < 20) return '十' + (n === 10 ? '' : CN_NUMS[n - 10])
  if (n < 100) {
    const tens = Math.floor(n / 10)
    const ones = n % 10
    return CN_NUMS[tens] + '十' + (ones === 0 ? '' : CN_NUMS[ones])
  }
  return String(n)
}

let _uidSeq = 1

export default {
  name: 'ReportGenSummary',
  components: { ReportGenSummaryCourse },
  props: {
    rrmaId: { type: [Number, String], default: null }
  },
  data () {
    return {
      detailLoading: false,
      exportLoading: false,
      previewLoading: false,
      previewDialogVisible: false,
      paramSaving: false,
      currentRecord: null,
      // 报告参数（保存到主表；mainTeacher 后端接口暂未放开，先展示+回显）
      paramForm: {
        courseName: '',
        mainTeacher: '',
        customerName: '',
        trainObject: '',
        projectManagerTeam: '',
        projectTeamContact: '',
        trainPlace: ''
      },
      // 课程内容记录列表
      courseList: [],
      currentIndex: 0,
      courseListBusy: false,
      // 右侧 AI 结果
      mainAi: { trainGoal: '', trainMethod: '', courseBackground: '', courseSummary: '', improveSuggestionOne: '', improveSuggestionTwo: '' },
      // 课程总结 / 讲师简介 编辑
      isAiEditing: false,
      aiEditTarget: '', // 'summary' | 'teacher'
      aiEditSaving: false,
      // AI 正文（培训目标等5项）编辑
      isAiParamEditing: false,
      aiParamSaving: false,
      aiParamEditForm: { trainGoal: '', trainMethod: '', courseBackground: '', improveSuggestionOne: '', improveSuggestionTwo: '' },
      // 课程 AI 生成中（控制右侧卡片 loading 蒙层）
      courseAiLoading: false,
      // 报告参数必填校验错误标记（用于输入框标红）
      paramErrors: {
        courseName: false,
        customerName: false,
        trainObject: false,
        projectManagerTeam: false,
        projectTeamContact: false,
        trainPlace: false
      },
      // 多份评估表时，导出/预览前选择用弹窗（用真正的 el-dialog + v-model 保证响应式）
      ratingPickVisible: false,
      ratingPickForm: { selectedId: null },
      ratingPickList: [],
      ratingPickResolve: null // resolve 回调缓存
    }
  },
  computed: {
    currentCourse () {
      return this.courseList[this.currentIndex] || null
    },
    hasAiResult () {
      return !!(this.mainAi.trainGoal || this.mainAi.trainMethod || this.mainAi.courseBackground ||
        this.mainAi.courseSummary || this.mainAi.improveSuggestionOne || this.mainAi.improveSuggestionTwo)
    },
    // 是否有任何课程已生成 AI 结果（用于课程总结/讲师简介卡片空状态判断）
    hasAnyCourseAi () {
      return this.courseList.some(c => !!(c.wordCourseSummary || c.wordTeacherAi))
    },
    // 多课程「课程总结」合并展示文本
    mergedCourseSummary () {
      if (this.courseList.length === 1) return this.courseList[0].wordCourseSummary || ''
      return this.courseList
        .map((c, i) => {
          const txt = (c.wordCourseSummary || '').trim()
          if (!txt) return ''
          return this.courseList.length > 1 ? `【课程${toChineseNumber(i + 1)}】\n${txt}` : txt
        })
        .filter(Boolean)
        .join('\n\n')
    },
    // 多课程「讲师简介（AI）」合并展示文本
    mergedTeacherAi () {
      if (this.courseList.length === 1) return this.courseList[0].wordTeacherAi || ''
      return this.courseList
        .map((c, i) => {
          const txt = (c.wordTeacherAi || '').trim()
          if (!txt) return ''
          return `【课程${toChineseNumber(i + 1)}】\n${txt}`
        })
        .filter(Boolean)
        .join('\n\n')
    }
  },
  watch: {
    rrmaId: {
      immediate: true,
      handler (val) {
        if (val) this.loadMergeRecord()
      }
    }
  },
  methods: {
    chineseNumber: toChineseNumber,

    async loadMergeRecord () {
      if (!this.rrmaId) return
      this.detailLoading = true
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        const record = Array.isArray(res.data) ? res.data[res.data.length - 1] : res.data
        if (!record || !record.rrmusmId) {
          this.currentRecord = null
          return
        }
        this.currentRecord = record
        // 解析主表报告参数（wordTextUser JSON）
        this.parseParamForm(record)
        // 解析主表 AI 正文（wordTextAi JSON）
        this.parseMainAi(record)
        // 加载课程内容记录列表
        await this.loadCourseList()
      } catch (e) {
        this.currentRecord = null
      } finally {
        this.detailLoading = false
      }
    },

    // 解析主表报告参数（新结构：去掉课程大纲/讲师等已下沉字段，仅保留通用参数）
    parseParamForm (detail) {
      if (detail.wordTextUser) {
        try {
          const obj = JSON.parse(detail.wordTextUser)
          this.paramForm.courseName = obj.courseName || ''
          // 主讲老师：后端接口暂未放开保存，但实体有该字段，先做回显
          this.paramForm.mainTeacher = obj.mainTeacher || ''
          this.paramForm.customerName = obj.customerName || ''
          this.paramForm.trainObject = obj.trainObject || ''
          this.paramForm.projectManagerTeam = obj.projectManagerTeam || ''
          this.paramForm.projectTeamContact = obj.projectTeamContact || ''
          this.paramForm.trainPlace = obj.trainPlace || ''
        } catch (e) { /* 忽略 */ }
      }
    },

    // 解析主表 AI 正文结果
    parseMainAi (detail) {
      if (detail.wordTextAi) {
        try {
          const obj = JSON.parse(detail.wordTextAi)
          this.mainAi.trainGoal = obj.trainGoal || ''
          this.mainAi.trainMethod = obj.trainMethod || ''
          this.mainAi.courseBackground = obj.courseBackground || ''
          // 主表的 courseSummary 作为兜底（多条课程时由后端汇总）
          this.mainAi.courseSummary = obj.courseSummary || ''
          this.mainAi.improveSuggestionOne = obj.improveSuggestionOne || ''
          this.mainAi.improveSuggestionTwo = obj.improveSuggestionTwo || ''
        } catch (e) { /* 忽略 */ }
      }
    },

    // 加载课程内容记录列表
    async loadCourseList () {
      if (!this.currentRecord || !this.currentRecord.rrmusmId) return
      try {
        const res = await getRtReportModelUserSignMergeWordList({ rrmusmId: this.currentRecord.rrmusmId })
        const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : [])
        this.courseList = list.map(item => this.normalizeCourse(item))
        this.currentIndex = 0
        // 若无任何课程记录，自动创建一条空课程占位
        if (!this.courseList.length) {
          this.courseList.push(this.createEmptyCourse())
        }
      } catch (e) {
        this.courseList = [this.createEmptyCourse()]
      }
    },

    // 归一化课程记录（追加前端辅助字段）
    normalizeCourse (item) {
      return {
        ...item,
        _uid: _uidSeq++,
        _editWordCourseSummary: item.wordCourseSummary || '',
        _editWordTeacherAi: item.wordTeacherAi || ''
      }
    },
    createEmptyCourse () {
      return {
        _uid: _uidSeq++,
        rrmusmWordId: null,
        rrmusmId: this.currentRecord ? this.currentRecord.rrmusmId : null,
        wordMainTeacher: '',
        wordTeacherUser: '',
        wordCourseName: '',
        wordCourseOutline: '',
        wordCourseOther: '',
        wordUserPrompt: '',
        wordTextAi: null,
        wordTeacherAi: '',
        wordCourseSummary: '',
        _editWordCourseSummary: '',
        _editWordTeacherAi: ''
      }
    },

    // 课程状态判定（供轮播 tab 显示）
    courseStatusOf (course) {
      if (!course.rrmusmWordId) return 'unsaved'
      if (!course.wordTextAi) return 'draft'
      return 'saved'
    },
    courseStatusText (course) {
      const t = this.courseStatusOf(course)
      if (t === 'unsaved') return '未保存'
      if (t === 'draft') return '已暂存'
      return '已生成'
    },

    // 切换当前课程
    switchCourse (idx) {
      if (idx < 0 || idx >= this.courseList.length) return
      this.currentIndex = idx
    },

    // 新增课程
    handleAddCourse () {
      this.courseList.push(this.createEmptyCourse())
      this.currentIndex = this.courseList.length - 1
      this.$message.success('已新增课程' + toChineseNumber(this.courseList.length) + '，请填写后保存')
    },

    // 删除课程（后端：第一个不允许删除）
    async handleDeleteCourse (course, idx) {
      if (idx === 0) {
        this.$message.warning('第一个课程不允许删除')
        return
      }
      try {
        await this.$confirm(`确认删除课程${toChineseNumber(idx + 1)}？该课程的所有内容将被清除。`, '删除课程', { type: 'warning' })
      } catch (e) { return }
      // 尚未提交到后端的新课程，直接前端移除
      if (!course.rrmusmWordId) {
        this.courseList.splice(idx, 1)
        if (this.currentIndex >= this.courseList.length) this.currentIndex = this.courseList.length - 1
        return
      }
      try {
        await deleteRtReportModelUserSignMergeWord({ rrmusmWordId: course.rrmusmWordId })
        this.$message.success('删除成功')
        this.courseList.splice(idx, 1)
        if (this.currentIndex >= this.courseList.length) this.currentIndex = this.courseList.length - 1
        // 删除后可能影响主表 AI 汇总，重新加载主表数据
        await this.refreshMainRecord()
      } catch (e) {
        this.$message.error(e.message || '删除失败，请稍后重试')
      }
    },

    // 子课程保存后的回调
    async onCourseSaved (newRecord, isSubmit) {
      // 用返回的最新记录替换当前课程
      const idx = this.currentIndex
      const normalized = this.normalizeCourse(newRecord)
      this.$set(this.courseList, idx, normalized)
      // 触发 AI 生成后，主表 wordTextAi / wordCourseSummary 可能被后端汇总更新，刷新主表
      if (isSubmit) {
        await this.refreshMainRecord()
      }
    },

    onCourseBusy (busy) {
      this.courseListBusy = busy
    },
    // 课程 AI 生成状态变化：控制右侧 loading 蒙层
    onCourseAiGenerating (loading) {
      this.courseAiLoading = loading
    },

    // 子组件请求 AI 生成：先校验+保存报告参数，成功后回调子组件调优化接口
    async onCourseRequestAi () {
      // 1. 校验报告参数必填项
      const requiredFields = [
        { key: 'courseName', label: '课程名称' },
        { key: 'customerName', label: '客户名称' },
        { key: 'trainObject', label: '培训对象' },
        { key: 'projectManagerTeam', label: '项目经理团队' },
        { key: 'projectTeamContact', label: '项目团队联系方式' },
        { key: 'trainPlace', label: '培训地点' }
      ]
      // 先重置错误标记
      Object.keys(this.paramErrors).forEach(k => { this.paramErrors[k] = false })
      let firstMissing = null
      requiredFields.forEach(f => {
        if (!(this.paramForm[f.key] || '').trim()) {
          this.paramErrors[f.key] = true
          if (!firstMissing) firstMissing = f
        }
      })
      if (firstMissing) {
        this.$message.warning(`请填写${firstMissing.label}`)
        this.scrollToParamField(firstMissing.key)
        // 校验失败：关闭 loading，让子组件也复位
        this.courseAiLoading = false
        if (this.$refs.courseEditor) {
          this.$refs.courseEditor.submitLoading = false
          this.$refs.courseEditor.courseListBusy = false
        }
        return
      }
      // 2. 调用保存参数接口
      try {
        await updateRtReportModelUserSignMergeWordParam({
          rrmusmId: this.currentRecord.rrmusmId,
          ...this.paramForm
        })
        // 3. 保存成功后，回调子组件调 AI 优化接口
        if (this.$refs.courseEditor) {
          await this.$refs.courseEditor.doSubmitAi()
        }
      } catch (e) {
        this.$message.error(e.message || '参数保存失败，请稍后重试')
        this.courseAiLoading = false
        if (this.$refs.courseEditor) {
          this.$refs.courseEditor.submitLoading = false
        }
      }
    },

    // 滚动到报告参数指定字段
    scrollToParamField (key) {
      this.$nextTick(() => {
        const refName = `paramItem-${key}`
        const el = this.$refs[refName]
        if (el) {
          if (el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          el.classList.add('is-flash')
          setTimeout(() => el.classList.remove('is-flash'), 1600)
        }
      })
    },

    // 清除报告参数单个字段错误标记
    clearParamError (key) {
      this.paramErrors[key] = false
    },

    // 重新拉取主表数据（刷新 AI 正文 / 参数）
    async refreshMainRecord () {
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        const record = Array.isArray(res.data) ? res.data[res.data.length - 1] : res.data
        if (record && record.rrmusmId) {
          this.currentRecord = record
          this.parseMainAi(record)
        }
      } catch (e) { /* 忽略 */ }
    },

    // 保存报告参数（主表）
    async handleSaveParam () {
      if (!this.currentRecord || !this.currentRecord.rrmusmId) {
        this.$message.warning('暂无可操作的记录')
        return
      }
      if (!this.paramForm.courseName.trim()) { this.$message.warning('请填写课程名称'); return }
      if (!this.paramForm.customerName.trim()) { this.$message.warning('请填写客户名称'); return }
      if (!this.paramForm.trainObject.trim()) { this.$message.warning('请填写培训对象'); return }
      if (!this.paramForm.projectManagerTeam.trim()) { this.$message.warning('请填写项目经理团队'); return }
      if (!this.paramForm.projectTeamContact.trim()) { this.$message.warning('请填写项目团队联系方式'); return }
      if (!this.paramForm.trainPlace.trim()) { this.$message.warning('请填写培训地点'); return }
      this.paramSaving = true
      try {
        await updateRtReportModelUserSignMergeWordParam({
          rrmusmId: this.currentRecord.rrmusmId,
          ...this.paramForm
        })
        // 注：主讲老师(mainTeacher)后端接口暂未放开，本次保存不会持久化该字段
        this.$message.success('参数保存成功')
      } catch (e) {
        this.$message.error(e.message || '参数保存失败，请稍后重试')
      } finally {
        this.paramSaving = false
      }
    },

    // ============ 右侧 AI 正文（培训目标等5项）编辑 ============
    startEditAiParam () {
      this.aiParamEditForm = {
        trainGoal: this.mainAi.trainGoal || '',
        trainMethod: this.mainAi.trainMethod || '',
        courseBackground: this.mainAi.courseBackground || '',
        improveSuggestionOne: this.mainAi.improveSuggestionOne || '',
        improveSuggestionTwo: this.mainAi.improveSuggestionTwo || ''
      }
      this.isAiParamEditing = true
    },
    cancelEditAiParam () {
      this.isAiParamEditing = false
    },
    // 保存 AI 正文到主表 wordTextAi（沿用旧版 updateRtReportModelUserSignMergeWordText 接口）
    async saveEditAiParam () {
      if (!this.currentRecord || !this.currentRecord.rrmusmId) {
        this.$message.warning('暂无可操作的记录')
        return
      }
      this.aiParamSaving = true
      try {
        await updateRtReportModelUserSignMergeWordText({
          rrmusmId: this.currentRecord.rrmusmId,
          trainGoal: this.aiParamEditForm.trainGoal,
          trainMethod: this.aiParamEditForm.trainMethod,
          courseBackground: this.aiParamEditForm.courseBackground,
          courseSummary: this.mainAi.courseSummary || '',
          improveSuggestionOne: this.aiParamEditForm.improveSuggestionOne,
          improveSuggestionTwo: this.aiParamEditForm.improveSuggestionTwo
        })
        // 同步到本地展示
        this.mainAi.trainGoal = this.aiParamEditForm.trainGoal
        this.mainAi.trainMethod = this.aiParamEditForm.trainMethod
        this.mainAi.courseBackground = this.aiParamEditForm.courseBackground
        this.mainAi.improveSuggestionOne = this.aiParamEditForm.improveSuggestionOne
        this.mainAi.improveSuggestionTwo = this.aiParamEditForm.improveSuggestionTwo
        this.isAiParamEditing = false
        this.$message.success('保存成功')
      } catch (e) {
        this.$message.error(e.message || '保存失败，请稍后重试')
      } finally {
        this.aiParamSaving = false
      }
    },

    // ============ 右侧 AI 结果编辑（课程总结 / 讲师简介） ============
    startEditAi (target) {
      // 进入编辑前同步最新的合并值到每个课程的编辑字段
      this.courseList.forEach((c, i) => {
        c._editWordCourseSummary = c.wordCourseSummary || ''
        c._editWordTeacherAi = c.wordTeacherAi || ''
      })
      this.aiEditTarget = target
      this.isAiEditing = true
    },
    cancelEditAi () {
      this.isAiEditing = false
      this.aiEditTarget = ''
    },
    // 批量保存：逐条调用 updateRtReportModelUserSignMergeWord
    async saveEditAi () {
      this.aiEditSaving = true
      try {
        const fieldKey = this.aiEditTarget === 'summary' ? '_editWordCourseSummary' : '_editWordTeacherAi'
        const paramName = this.aiEditTarget === 'summary' ? 'wordCourseSummary' : 'wordTeacherAi'
        for (const c of this.courseList) {
          if (!c.rrmusmWordId) continue // 未保存的新课程跳过
          await updateRtReportModelUserSignMergeWord({
            rrmusmWordId: c.rrmusmWordId,
            [paramName]: c[fieldKey] || ''
          })
          // 同步回主字段
          c[paramName] = c[fieldKey] || ''
        }
        this.$message.success('保存成功')
        this.isAiEditing = false
        this.aiEditTarget = ''
      } catch (e) {
        this.$message.error(e.message || '保存失败，请稍后重试')
      } finally {
        this.aiEditSaving = false
      }
    },

    // ============ 预览 / 生成报告 ============
    handlePreviewReport () {
      if (!this.currentRecord || !this.currentRecord.rrmusmId) {
        this.$message.warning('暂无记录')
        return
      }
      const file = this.currentRecord.rtReportModelUserSignMergeFile
      if (file && (file.rrmusmfFilePath || file.rrmusmfFileName)) {
        this.previewDialogVisible = true
      } else {
        this.doPreview(2)
      }
    },
    // 当评估表存在多条时，让用户选择导出/预览哪一条评估；0或1条时直接放行
    // 返回 Promise：resolve(选中的 rrmusmRatingId 或 null)，reject 表示用户取消
    pickRatingIfNeeded () {
      const list = (this.currentRecord && this.currentRecord.rtReportModelUserSignMergeRatingList) || []
      if (!Array.isArray(list) || list.length <= 1) {
        // 0 或 1 条：直接返回，无需弹窗
        return Promise.resolve(list.length === 1 ? list[0].rrmusmRatingId : null)
      }
      // 多条：打开组件级 el-dialog 让用户选择（v-model 响应式可靠）
      return new Promise((resolve, reject) => {
        this.ratingPickList = list
        this.ratingPickForm.selectedId = list[0].rrmusmRatingId
        this.ratingPickResolve = { resolve, reject }
        this.ratingPickVisible = true
      })
    },
    // 评估选择弹窗 - 确定
    handleRatingPickConfirm () {
      const id = this.ratingPickForm.selectedId
      this.ratingPickVisible = false
      const cb = this.ratingPickResolve
      this.ratingPickResolve = null
      if (cb) cb.resolve(id)
    },
    // 评估选择弹窗 - 取消
    handleRatingPickCancel () {
      this.ratingPickVisible = false
      const cb = this.ratingPickResolve
      this.ratingPickResolve = null
      if (cb) cb.reject(new Error('cancel'))
    },
    handlePreviewConfirm (operationTypeRepeat) {
      this.previewDialogVisible = false
      this.doPreview(operationTypeRepeat)
    },
    async doPreview (operationTypeRepeat) {
      let rrmusmRatingId = null
      // 多份评估时让用户选择
      try {
        rrmusmRatingId = await this.pickRatingIfNeeded()
      } catch (e) {
        return // 用户取消选择
      }
      this.previewLoading = true
      try {
        const res = await previewRtReportModelUserSignMergeReportWord({
          rrmusmId: this.currentRecord.rrmusmId,
          operationTypeRepeat,
          rrmusmRatingId
        })
        const data = res.data || {}
        if (data.rrmusmfFilePath) {
          this.$router.push({
            path: '/reportApply/reportOnlyOffice',
            query: { fileUrl: data.rrmusmfFilePath, fileName: data.rrmusmfFileName || '总结报告.docx' }
          })
        } else {
          this.$message.warning('未获取到预览文件地址')
        }
      } catch (e) {
        this.$message.error(e.message || '预览失败，请稍后重试')
      } finally {
        this.previewLoading = false
      }
    },
    async handleExportWord () {
      if (!this.currentRecord || !this.currentRecord.rrmusmId) {
        this.$message.warning('暂无可导出的报告')
        return
      }
      let rrmusmRatingId = null
      // 多份评估时让用户选择导出哪一份评估对应的报告
      try {
        rrmusmRatingId = await this.pickRatingIfNeeded()
      } catch (e) {
        return // 用户取消选择
      }
      this.exportLoading = true
      try {
        await exportRtReportModelUserSignMergeReportWord({
          rrmusmId: this.currentRecord.rrmusmId,
          rrmusmRatingId
        })
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error(e.message || '导出失败，请稍后重试')
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.gen-summary { min-height: 260px; }

// 空状态
.gen-summary-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 64px 24px;
}
.gen-summary-empty-icon {
  width: 72px; height: 72px; border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 20px; box-shadow: 0 4px 12px rgba(217, 119, 6, 0.1);
  i { font-size: 32px; color: #d97706; }
}
.gen-summary-empty-title { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.gen-summary-empty-desc { font-size: 13px; color: #64748b; }

// 横向行布局：左卡片 → 箭头 → 右卡片
.gen-summary-row {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-bottom: 20px;
  // 让左右卡片等宽分布，箭头自适应
  > .gen-summary-card { flex: 1; min-width: 0; }
}
.gen-summary-toolbar { display: flex; justify-content: flex-end; gap: 10px; margin-bottom: 16px; }

// 中间箭头
.gen-summary-arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  align-self: stretch;
  color: #94a3b8;
  i {
    font-size: 22px;
    font-weight: bold;
    color: #6366f1;
    animation: arrowPulse 2s ease-in-out infinite;
  }
}
@keyframes arrowPulse {
  0%, 100% { opacity: 0.5; transform: translateX(0); }
  50% { opacity: 1; transform: translateX(3px); }
}

// 卡片
.gen-summary-card {
  position: relative; // 作为 AI loading 蒙层的定位上下文
  border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #fff;
}
.gen-summary-card--ai {
  background:
    radial-gradient(circle at 92% 8%, rgba(16, 185, 129, 0.06), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(240, 253, 244, 0.94));
  border-color: rgba(16, 185, 129, 0.24);
}
.gen-summary-card-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}
.gen-summary-card--ai .gen-summary-card-header {
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
  border-bottom-color: rgba(16, 185, 129, 0.16);
}
.gen-summary-card-title {
  font-size: 14px; font-weight: 600; color: #1e293b;
  display: flex; align-items: center; gap: 6px;
  i { color: #4f7cff; font-size: 16px; }
}
.gen-summary-card--ai .gen-summary-card-title i { color: #10b981; }
.gen-summary-card-hint { font-size: 11px; color: #94a3b8; }
.gen-summary-card-header-ops { display: flex; align-items: center; gap: 8px; }

// 新增课程按钮图标（避免被全局 business-actions 的浅蓝色覆盖导致看不清）
.gen-course-ops .el-button--primary .gen-course-add-icon,
.gen-course-ops .el-button--primary .el-icon-plus {
  color: #ffffff !important;
}
.gen-course-add-icon {
  margin-right: 2px;
}
.gen-summary-card-body { padding: 16px 18px; }

// 课程总结/讲师简介卡片：与左侧课程信息等高，内部两块平分高度
.gen-summary-card--merge {
  display: flex;
  flex-direction: column;
  // 卡片高度由行 stretch 决定（跟随左侧课程信息）
}
.gen-summary-card-body--merge {
  flex: 1;
  min-height: 0; // 关键：允许子元素收缩滚动
  display: flex;
  flex-direction: column;
}

// AI 空状态
.gen-summary-ai-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 64px 24px;
}
.gen-summary-ai-empty-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px;
  i { font-size: 26px; color: #10b981; }
}
.gen-summary-ai-empty-text { font-size: 14px; font-weight: 600; color: #475569; margin-bottom: 6px; }
.gen-summary-ai-empty-hint { font-size: 12px; color: #94a3b8; text-align: center; }

// 区块
.gen-summary-section { margin-bottom: 14px; &:last-child { margin-bottom: 0; } }
.gen-summary-label {
  font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 8px;
  &--required::before { content: '* '; color: #f56c6c; }
  &--sub { font-size: 12px; font-weight: 500; color: #64748b; margin-top: 10px; }
}
// 字段标题旁的小提示
.gen-summary-label__tip {
  margin-left: 6px;
  font-size: 11px;
  font-weight: 400;
  color: #94a3b8;
}
.gen-summary-sublabel { font-size: 12px; font-weight: 500; color: #64748b; margin-bottom: 6px; }
.gen-summary-readonly {
  font-size: 13px; line-height: 1.7; color: #1e293b;
  white-space: pre-wrap; word-break: break-word;
  padding: 8px 12px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0;
  min-height: 36px;
  &--merge {
    background: linear-gradient(135deg, #f0fdf4, #ffffff);
    border-color: rgba(16, 185, 129, 0.18);
    // 在平分高度的 merge-block 内自适应剩余空间并滚动
    flex: 1 1 auto;
    min-height: 60px;
    overflow-y: auto;
  }
}

// 表单堆叠（每个字段独占一行）
.gen-summary-form-stack { display: flex; flex-direction: column; gap: 12px; }
.gen-summary-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.gen-summary-form-item { min-width: 0; position: relative; }

// 必填校验：未填字段输入框边框变红
.gen-summary-form-item.is-error {
  ::v-deep .el-input__inner,
  ::v-deep .el-textarea__inner {
    border-color: #f56c6c !important;
    background: #fef0f0 !important;
    &:focus { border-color: #f56c6c !important; }
  }
}
// 滚动定位后的高亮闪烁
.gen-summary-form-item.is-flash {
  animation: paramFlash 1.6s ease-out;
}
@keyframes paramFlash {
  0%   { background: rgba(245, 108, 108, 0.25); box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.15); }
  30%  { background: rgba(245, 108, 108, 0.12); }
  100% { background: transparent; box-shadow: none; }
}
.gen-summary-field-hint {
  position: absolute; top: 0; right: 0; padding: 2px 8px;
  font-size: 11px; font-weight: 500; color: #92400e;
  background: #fef3c7; border-radius: 4px;
}

// 操作按钮
.gen-summary-action-btns { display: flex; gap: 10px; margin-top: 12px; }

// ===== 课程卡片轮播 =====
.gen-course-ops { display: flex; gap: 8px; }

.gen-course-carousel {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f5f7fa, #ffffff);
  border-bottom: 1px solid #e8ecf1;
}
.gen-course-arrow { flex-shrink: 0; }
.gen-course-track {
  flex: 1; min-width: 0;
  display: flex; gap: 8px; overflow-x: auto;
  &::-webkit-scrollbar { height: 4px; }
}
.gen-course-tab {
  flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 16px;
  background: #fff; border: 1px solid #e2e8f0;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #c7d2fe; }
  &.is-active {
    background: linear-gradient(135deg, #4f7cff, #6366f1);
    border-color: transparent;
    .gen-course-tab__name, .gen-course-tab__status, .gen-course-tab__del { color: #fff; }
    .gen-course-tab__status.is-dirty { background: rgba(255,255,255,0.25); color: #fff; }
    .gen-course-tab__status.is-unsaved { background: rgba(255,255,255,0.25); color: #fff; }
    .gen-course-tab__status.is-draft { background: rgba(255,255,255,0.25); color: #fff; }
    .gen-course-tab__status.is-saved { background: rgba(255,255,255,0.25); color: #fff; }
  }
}
.gen-course-tab__name { font-size: 12px; font-weight: 600; color: #1e293b; }
.gen-course-tab__status {
  font-size: 10px; padding: 1px 6px; border-radius: 8px;
  &.is-dirty { background: #fef3c7; color: #92400e; }
  &.is-unsaved { background: #f1f5f9; color: #64748b; }
  &.is-draft { background: #dbeafe; color: #1d4ed8; }
  &.is-saved { background: #d1fae5; color: #047857; }
}
.gen-course-tab__del {
  font-size: 12px; color: #94a3b8; margin-left: 2px;
  &:hover { color: #ef4444; }
}

.gen-course-current-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 14px; font-weight: 600; color: #1e293b;
  margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px dashed #e2e8f0;
  i { color: #4f7cff; font-size: 16px; }
}
.gen-course-current-title__hint { font-size: 11px; font-weight: 400; color: #94a3b8; margin-left: 4px; }

// ===== 右侧合并展示区 =====
.gen-summary-merge-block {
  margin-top: 16px; padding-top: 14px; border-top: 1px dashed #e2e8f0;
  // 在 merge 卡片内：两块平分高度，各自独立滚动
  display: flex;
  flex-direction: column;
  min-height: 0;
}
// 第一个块去掉顶部边框和间距（它在 body 顶部）
.gen-summary-card-body--merge > .gen-summary-merge-block:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
// merge 卡片内：两个块各占一半
.gen-summary-card-body--merge > .gen-summary-merge-block {
  flex: 1 1 0;
}
.gen-summary-merge-block__title {
  display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
  font-size: 13px; font-weight: 600; color: #1e293b;
  i { color: #10b981; font-size: 15px; }
}
.gen-summary-merge-block__hint {
  flex: 1; font-size: 11px; font-weight: 400; color: #94a3b8;
}

// ===== 编辑模式（多课程拆分） =====
.gen-summary-edit-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 12px; margin-bottom: 14px;
  background: #fff7ed; border: 1px solid #fed7aa; border-radius: 6px;
  font-size: 12px; color: #9a3412;
  i { color: #f97316; }
}
.gen-summary-edit-item {
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
}

// AI 生成结果卡片：预览只读区 + 编辑 textarea 都限制最大高度并滚动
.gen-summary-card-body--aiparam {
  // 预览模式：只读内容块
  .gen-summary-readonly {
    max-height: 160px;
    overflow-y: auto;
  }
  // 编辑模式：textarea
  ::v-deep .el-textarea__inner {
    max-height: 180px;
    overflow-y: auto;
    resize: vertical;
  }
}

// ===== AI 生成中特效 loading 蒙层 =====
.gen-ai-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

// 半透明渐变遮罩（毛玻璃）
.gen-ai-loading__overlay {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 50% 45%, rgba(16, 185, 129, 0.08), transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.72), rgba(240, 253, 244, 0.82));
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: aiOverlayFade 0.3s ease;
}
@keyframes aiOverlayFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

// 外圈光晕呼吸
.gen-ai-loading__halo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 160px;
  height: 160px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.22), transparent 70%);
  filter: blur(8px);
  animation: aiHaloPulse 2s ease-in-out infinite;
}
@keyframes aiHaloPulse {
  0%, 100% { transform: translate(-50%, -50%) scale(0.85); opacity: 0.55; }
  50%      { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
}

// 核心：双旋转环 + 中心图标
.gen-ai-loading__core {
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
}

// 旋转环（顺时针）
.gen-ai-loading__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #6366f1;
  border-right-color: #10b981;
  animation: aiRingSpin 1.2s linear infinite;
}
// 反向旋转环
.gen-ai-loading__ring--reverse {
  inset: 10px;
  border-top-color: transparent;
  border-bottom-color: #f59e0b;
  border-left-color: #ec4899;
  animation: aiRingSpinReverse 1.6s linear infinite;
}
@keyframes aiRingSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes aiRingSpinReverse {
  from { transform: rotate(360deg); }
  to   { transform: rotate(0deg); }
}

// 中心魔法棒图标（轻微浮动）
.gen-ai-loading__icon {
  position: relative;
  z-index: 2;
  font-size: 30px;
  color: #6366f1;
  animation: aiIconFloat 1.4s ease-in-out infinite;
}
@keyframes aiIconFloat {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
}

// 文案
.gen-ai-loading__text {
  position: relative;
  z-index: 2;
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.gen-ai-loading__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: 1px;
}
.gen-ai-loading__sub {
  font-size: 12px;
  color: #64748b;
}
// 三个延迟渐显的点
.gen-ai-loading__dot {
  display: inline-block;
  font-style: normal;
  opacity: 0;
  animation: aiDotBlink 1.4s infinite;
}
.gen-ai-loading__dot:nth-child(1) { animation-delay: 0s; }
.gen-ai-loading__dot:nth-child(2) { animation-delay: 0.2s; }
.gen-ai-loading__dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes aiDotBlink {
  0%, 60%, 100% { opacity: 0; }
  30%           { opacity: 1; }
}

// 轨道粒子（8 个环绕旋转）
.gen-ai-loading__particles {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 140px;
  height: 140px;
  transform: translate(-50%, -50%);
  animation: aiParticlesRotate 6s linear infinite;
}
.gen-ai-loading__particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  margin: -3px 0 0 -3px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.8);
}
// 8 个粒子均匀分布到圆周上
.gen-ai-loading__particle:nth-child(1) { transform: rotate(0deg)   translateX(70px); background: #6366f1; }
.gen-ai-loading__particle:nth-child(2) { transform: rotate(45deg)  translateX(70px); background: #10b981; }
.gen-ai-loading__particle:nth-child(3) { transform: rotate(90deg)  translateX(70px); background: #f59e0b; }
.gen-ai-loading__particle:nth-child(4) { transform: rotate(135deg) translateX(70px); background: #ec4899; }
.gen-ai-loading__particle:nth-child(5) { transform: rotate(180deg) translateX(70px); background: #6366f1; }
.gen-ai-loading__particle:nth-child(6) { transform: rotate(225deg) translateX(70px); background: #10b981; }
.gen-ai-loading__particle:nth-child(7) { transform: rotate(270deg) translateX(70px); background: #f59e0b; }
.gen-ai-loading__particle:nth-child(8) { transform: rotate(315deg) translateX(70px); background: #ec4899; }
@keyframes aiParticlesRotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
}
</style>
